import { Box, Text, Flex } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState, useEffect, useRef, useMemo } from 'react';

// Task-specific thinking steps based on current onboarding step
// Each step lists the actual tasks being performed
const STEP_MESSAGES: Record<string, string[]> = {
	// Initial thinking state - shown before backend tells us what it's doing
	thinking: [
		'Thinking...',
		'Let me figure this out...',
		'Hmm, interesting...',
		'Processing your request...',
		'Give me a sec...',
		'On it!',
		'Working on it...',
		'Let me see...',
		'One moment...',
		'Analyzing...',
		'Putting the pieces together...',
		'Crunching the numbers...',
		'Almost there...',
	],
	// Welcome / initial (new conversation)
	welcome: [
		'Starting up...',
		'Loading your account...',
		'Getting ready...',
		'Rolling out the red carpet...',
		'Warming up the engines...',
		'Preparing your workspace...',
		'Almost there...',
		'Setting the stage...',
	],
	// Resuming an existing conversation
	resume: [
		'Loading conversation...',
		'Restoring your progress...',
		'Getting back on track...',
		'Picking up where we left off...',
		'Refreshing my memory...',
		'Rewinding the tape...',
		'Dusting off the notes...',
		'Back in action...',
	],
	// Website analysis
	website: [
		'Connecting to website...',
		'Downloading page content...',
		'Extracting business name...',
		'Finding brand colors...',
		'Looking for logo...',
		'Scanning for products...',
		'Reading contact info...',
		'Exploring your digital home...',
		'Playing detective on your site...',
		'Gathering all the good stuff...',
		'This is a nice website...',
		'Digging through the code...',
	],
	// Business name
	name: [
		'Got it!',
		'That\'s a great name!',
		'Saving your business name...',
		'Making it official...',
		'Locking it in...',
		'Sounds professional!',
		'Nice choice!',
		'Updating your profile...',
	],
	// Business description - user describes what they do
	description: [
		'Reading your description...',
		'Extracting key services...',
		'Identifying business type...',
		'Matching to industries...',
		'Saving business profile...',
		'Understanding your vibe...',
		'This sounds exciting!',
		'Taking notes...',
		'Love what you do!',
		'Capturing the essence...',
	],
	// Industry matching
	industry: [
		'Loading industry list...',
		'Analyzing business type...',
		'Finding best matches...',
		'Ranking by relevance...',
		'Saving industry selection...',
		'Consulting the industry map...',
		'Finding your tribe...',
		'Categorizing with care...',
		'Matching you up...',
		'Perfect fit coming up...',
	],
	// Phone number
	phone: [
		'Validating phone format...',
		'Checking country code...',
		'Saving contact info...',
		'Ring ring! Looks good...',
		'Making sure the digits work...',
		'Adding to your profile...',
		'You\'ve got mail... er, phone!',
		'Contact saved!',
	],
	// Logo generation - AI creates logo options
	logo: [
		'Analyzing brand style...',
		'Generating design concepts...',
		'Creating color variations...',
		'Rendering logo images...',
		'Uploading to storage...',
		'Preparing preview...',
		'Getting creative...',
		'Mixing colors like a pro...',
		'Designing something special...',
		'Art in progress...',
		'Making it look gorgeous...',
	],
	// Catalog import (from website scraping)
	catalogs_import: [
		'Scanning for products...',
		'Reading product details...',
		'Downloading images...',
		'Categorizing items...',
		'Saving to catalog...',
		'Shopping through your inventory...',
		'Ooh, nice products!',
		'Organizing the shelves...',
		'Snapping product photos...',
		'Building your catalog...',
	],
	// Adding a product manually
	catalogs_add: [
		'Adding your product...',
		'Creating your service...',
		'Saving to catalog...',
		'Setting up your offering...',
		'Got it!',
		'Nice product!',
		'Adding to the lineup...',
		'Building your catalog...',
	],
	// Catalog selection
	catalogs_selection: [
		'Loading catalog items...',
		'Updating selections...',
		'Saving preferences...',
		'Curating the best picks...',
		'Organizing your favorites...',
		'Sorting through options...',
		'Making it look pretty...',
		'Almost ready to show off...',
	],
	// Sales channel
	channel: [
		'Loading channel options...',
		'Configuring settings...',
		'Saving channel preference...',
		'Setting up shop...',
		'Opening for business...',
		'Connecting the dots...',
		'Choosing your adventure...',
		'Great choice!',
	],
	// Business locations
	locations: [
		'Parsing address...',
		'Looking up coordinates...',
		'Validating location...',
		'Saving to account...',
		'Dropping a pin...',
		'Finding you on the map...',
		'X marks the spot...',
		'Great location!',
		'Adding to the map...',
	],
	// Service areas
	service_areas: [
		'Calculating radius...',
		'Mapping coverage zone...',
		'Validating boundaries...',
		'Saving service area...',
		'Drawing the circle...',
		'Expanding your reach...',
		'Setting your turf...',
		'Defining your territory...',
		'Looking good on the map...',
	],
	// Google Business Profile
	google_business_profile: [
		'Connecting to Google...',
		'Fetching business data...',
		'Importing reviews...',
		'Syncing information...',
		'Talking to the Google gods...',
		'Grabbing your reviews...',
		'Pulling in the good stuff...',
		'Making Google happy...',
		'Syncing up nicely...',
	],
	// Customer profiles / audiences
	customer_profiles: [
		'Analyzing your business...',
		'Identifying target demographics...',
		'Building audience segments...',
		'Matching ad platforms...',
		'Creating customer profile...',
		'Finding your people...',
		'Who\'s gonna love this?',
		'Profiling your fans...',
		'Targeting the right crowd...',
		'Building your dream audience...',
	],
	// Pre-generation review
	pre_generation: [
		'Collecting account data...',
		'Validating requirements...',
		'Preparing for generation...',
		'Double-checking everything...',
		'Crossing the t\'s...',
		'Dotting the i\'s...',
		'Almost ready for liftoff...',
		'Final checks in progress...',
	],
	// Campaign generation
	generate_campaign: [
		'Analyzing your audience...',
		'Writing ad copy...',
		'Generating images...',
		'Creating ad variations...',
		'Optimizing for platforms...',
		'Saving campaign...',
		'Crafting marketing magic...',
		'Making ads that pop...',
		'This is gonna be good!',
		'Creating your masterpiece...',
		'Putting the pieces together...',
	],
	// Fast path campaign creation (after website scraping)
	creating_campaign: [
		'Creating your campaign...',
		'Setting up service areas...',
		'Building customer profiles...',
		'Analyzing your target audience...',
		'Generating ad creatives...',
		'Selecting best designs...',
		'Optimizing for your business...',
		'Crafting the perfect campaign...',
		'Almost ready to launch...',
		'Putting together your ads...',
		'This is going to look great!',
		'Fine-tuning the details...',
	],
	// Success / completion
	success: [
		'Finalizing campaign...',
		'Publishing changes...',
		'Wrapping it up with a bow...',
		'You did it!',
		'Celebrating success...',
		'Mission accomplished...',
		'Ready to rock!',
	],
	// Default/fallback - generic but still task-oriented
	default: [
		'Processing request...',
		'Analyzing data...',
		'Saving changes...',
		'Working on it...',
		'Crunching numbers...',
		'Almost there...',
		'Hang tight...',
		'Magic happening...',
		'Let me check on that...',
		'One moment please...',
		'Getting things ready...',
		'On the case...',
		'Making progress...',
		'Just a moment...',
	],
};

// Pulse animation for the dot
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

interface ThinkingMessageProps {
	currentStep?: string;
}

const ThinkingMessage = ({ currentStep }: ThinkingMessageProps) => {
	console.log('[ThinkingMessage] Rendering with currentStep:', currentStep);

	// Get messages for current step, fallback to default
	// Normalize step name (lowercase, replace spaces/hyphens with underscores)
	const normalizedStep = useMemo(() => {
		const normalized = currentStep?.toLowerCase().replace(/[-\s]/g, '_') || '';
		console.log('[ThinkingMessage] Normalized step:', normalized, '- has messages:', !!STEP_MESSAGES[normalized]);
		return normalized;
	}, [currentStep]);

	const messages = useMemo(() => {
		return STEP_MESSAGES[normalizedStep] || STEP_MESSAGES.default;
	}, [normalizedStep]);

	const [stepIndex, setStepIndex] = useState(0);
	const prevStepRef = useRef<string>(normalizedStep); // Initialize with current step
	const isFirstMount = useRef(true);

	// Only reset index when the step ACTUALLY changes (not on mount or re-render)
	useEffect(() => {
		// Skip the first mount - don't log or reset
		if (isFirstMount.current) {
			isFirstMount.current = false;
			prevStepRef.current = normalizedStep;
			return;
		}

		if (prevStepRef.current !== normalizedStep) {
			console.log('[ThinkingMessage] Step changed from', prevStepRef.current, 'to', normalizedStep);
			setStepIndex(0);
			prevStepRef.current = normalizedStep;
		}
	}, [normalizedStep]);

	// Rotate through messages every 5.5 seconds
	useEffect(() => {
		const interval = setInterval(() => {
			setStepIndex((prev) => (prev + 1) % messages.length);
		}, 5500);

		return () => clearInterval(interval);
	}, [messages.length]);

	return (
		<Box
			bg="gray.100"
			borderRadius="xl"
			px={4}
			py={2}
			maxW="280px"
		>
			<Flex alignItems="center" gap={2}>
				<Box
					w="8px"
					h="8px"
					borderRadius="full"
					bg="orange.400"
					animation={`${pulse} 1.5s ease-in-out infinite`}
				/>
				<Text
					fontSize="sm"
					color="gray.600"
					fontStyle="italic"
				>
					{messages[stepIndex]}
				</Text>
			</Flex>
		</Box>
	);
};

export default ThinkingMessage;
